import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

const navigation = [
  { name: "Work", route: "/portfolio" },
  { name: "Services", route: "/service" },
  { name: "Web scraping", route: "/webscraper" },
  { name: "Blog", route: "/blog" },
  { name: "About", route: "/about" },
  { name: "Contact", route: "/contact" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isActiveRoute = (route) => {
    if (route === "/") {
      return location.pathname === route;
    }

    return (
      location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
  };

  // Track window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMenu = () => {
    if (windowWidth <= 900) setIsOpen(!isOpen); // only toggle if <=1100px
  };

  return (
    <header className="bg-dark-900 border-b border-dark-600 text-light-100 fixed top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <span className="flex items-center gap-3 text-lg font-semibold tracking-tight">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-dark-900 text-sm font-bold">
                  AT
                </span>
                Aashish Timsina
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav
            className={
              windowWidth > 900 ? "flex items-center space-x-6 ml-auto" : "hidden"
            }
          >
            {navigation.map((item) => (
              <Link key={item.name} to={item.route}>
                <span
                  className={classNames(
                    "py-2 px-1 text-sm border-b-2 transition-colors",
                    isActiveRoute(item.route)
                      ? "text-light-100 border-primary-400"
                      : "text-gray-400 border-transparent hover:text-light-100",
                  )}
                >
                  {item.name}
                </span>
              </Link>
            ))}
          </nav>

          {windowWidth > 900 && (
            <Link
              to="/contact"
              className="ml-4 px-4 py-2 rounded-md bg-primary-500 text-dark-900 text-sm font-semibold hover:bg-primary-400 transition-colors"
            >
              Hire me
            </Link>
          )}

          {/* Mobile Menu Button */}
          {windowWidth <= 900 && (
            <motion.button
              onClick={toggleMenu}
              className="flex items-center p-2 rounded-md text-light-100 hover:text-primary-400 focus:outline-none"
              aria-label="Toggle menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </motion.button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {windowWidth <= 900 && (
        <motion.div
          className="bg-dark-800 border-t border-dark-600 overflow-hidden"
          initial={{ height: 0 }}
          animate={{ height: isOpen ? "auto" : 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-6 py-4 space-y-2">
            {navigation.map((item) => (
              <Link key={item.name} to={item.route}>
                <div
                  className={classNames(
                    "block px-4 py-3 rounded-md cursor-pointer",
                    isActiveRoute(item.route)
                      ? "text-light-100 bg-dark-700"
                      : "text-gray-400 hover:bg-dark-700 hover:text-light-100",
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
